import { useEffect, useState } from 'react';
import { ErrorBanner, Loading, PageHead } from '../components/ui';
import { useResource } from '../lib/hooks';
import type { EosSummary, Me } from '../lib/types';
import { Tabs } from './am/amUi';
import { EosHeadlines } from './eos/EosHeadlines';
import { EosIssues } from './eos/EosIssues';
import { EosMeetings } from './eos/EosMeetings';
import { EosOverview } from './eos/EosOverview';
import { EosRocks } from './eos/EosRocks';
import { EosScorecard } from './eos/EosScorecard';
import { EosSyncButton } from './eos/EosSync';
import { EosTeams } from './eos/EosTeams';
import { EosTemplates } from './eos/EosTemplates';
import { EosTodos } from './eos/EosTodos';
import type { EosRock, EosTeam, EosTodo } from './eos/eosTypes';
import { TeamPicker, eosQuarter } from './eos/eosUi';

/**
 * EOS — the Elliott Operating System, moved in from `C:\Development\EOS - Lite`.
 *
 * The operating cadence: weekly Level 10 meetings run against a timed agenda,
 * the 13-week scorecard and its reusable templates, quarterly rocks with
 * milestones, the issues list worked through IDS, seven-day to-dos, and
 * headlines.
 *
 * WHAT CAME ACROSS AND WHAT DID NOT
 *
 * Its `users` table did not. This estate has one staff list, `core.Employee`,
 * and this app owns it — the Teams tab picks people out of that directory
 * rather than keeping a second one. Same argument migration 048 made for the
 * admin app's `employees`.
 *
 * Its `rocks` and `issues` tables did not either, and this is the one to know
 * about while using the module: Rocks and Issues here read and write
 * `coo.Rock` and `coo.Issue` — the V/TO's. A rock set in a Level 10 appears on
 * the plan, and a rock set on the plan is reviewed in the meeting. Two rock
 * lists would mean the leadership team setting priorities in one place and
 * reviewing them against another, with no query able to reconcile the two.
 *
 * Its OML Assessment did not come across. This app already has one, richer and
 * template-driven, at /oml — four tables storing a JSON question set were not
 * worth landing beside it. Its accountability chart had already been removed
 * from its own navigation; this app's is at /chart.
 *
 * Behind the `eos` card AND the leadership gate. See App.tsx, and the double
 * middleware on `/api/eos` in the API's server.ts for why both.
 */

type Tab =
  | 'overview'
  | 'meetings'
  | 'scorecard'
  | 'rocks'
  | 'todos'
  | 'issues'
  | 'headlines'
  | 'templates'
  | 'teams';

export function EosPage({ me }: { me: Me | null }) {
  const isAdmin = me?.appRole === 'admin';
  const teams = useResource<EosTeam[]>('/eos/teams');
  const [teamId, setTeamId] = useState<number | null>(null);
  const [tab, setTab] = useState<Tab>('overview');
  /* Bumped by a sync, so every tab below remounts and reads afresh. */
  const [synced, setSynced] = useState(0);

  /* The picker opens on the default team once the list arrives, and stays where
     the person put it after that — re-deriving it on every render would snap
     the selection back the moment anything reloaded. */
  useEffect(() => {
    if (teamId !== null || !teams.data || teams.data.length === 0) return;
    const preferred = teams.data.find((t) => t.IsDefault) ?? teams.data[0];
    setTeamId(preferred?.EosTeamId ?? null);
  }, [teams.data, teamId]);

  const summary = useResource<EosSummary>(
    teamId === null ? '/eos/summary' : `/eos/summary?teamId=${teamId}`,
  );

  /* The dashboard shows this quarter's rocks and the to-dos due soonest as
     lists, not just as counts — that is the shape the app this came from
     opened on. Both are fetched here rather than inside the overview so the
     tab can stay a pure render, and so switching to Rocks or To-Dos finds the
     data already warm.

     Held to the current quarter deliberately: the panel is headed with the
     quarter, and last quarter's rocks under that heading would be wrong. */
  const rocks = useResource<EosRock[]>(
    teamId === null ? null : `/eos/rocks?teamId=${teamId}&quarter=${eosQuarter()}`,
  );
  const todos = useResource<EosTodo[]>(teamId === null ? null : `/eos/todos?teamId=${teamId}`);

  const reload = () => {
    summary.reload();
    teams.reload();
    rocks.reload();
    todos.reload();
  };

  const TABS: { key: Tab; label: string; count?: number }[] = [
    { key: 'overview', label: 'Dashboard' },
    { key: 'meetings', label: 'Meetings' },
    { key: 'scorecard', label: 'Scorecard' },
    { key: 'rocks', label: 'Rocks' },
    { key: 'todos', label: 'To-Dos', count: summary.data?.todos.open },
    { key: 'issues', label: 'Issues', count: summary.data?.issues.openShort },
    { key: 'headlines', label: 'Headlines' },
    /* Templates and Teams are administration rather than cadence, and both
       reach across every team — see the notes on those two tabs. */
    ...(isAdmin ? ([{ key: 'templates', label: 'Templates' }] as const) : []),
    ...(isAdmin ? ([{ key: 'teams', label: 'Teams' }] as const) : []),
  ];

  return (
    <>
      <PageHead
        title="EOS"
        lede="The operating cadence — Level 10 meetings, the scorecard, this quarter's rocks, the issues list and the to-dos that come out of them."
        actions={
          <>
            <EosSyncButton
              isAdmin={isAdmin}
              onSynced={() => {
                setSynced((n) => n + 1);
                reload();
              }}
            />
            <TeamPicker teams={teams.data ?? []} teamId={teamId} onChange={setTeamId} />
          </>
        }
      />

      {teams.error && <ErrorBanner message={teams.error} />}
      {teams.loading && <Loading label="Loading teams" />}

      <Tabs<Tab> tabs={TABS} active={tab} onChange={setTab} />

      {tab === 'overview' && (
        <EosOverview
          summary={summary.data}
          rocks={rocks.data ?? []}
          todos={todos.data ?? []}
          loading={summary.loading}
          error={summary.error}
          onOpenTab={setTab}
        />
      )}

      {/* Every tab below is scoped to one team, so none of them render until
          there is one. Without a team there is nothing to scope to and each
          would have to invent its own version of this check. */}
      {teamId === null
        ? tab !== 'overview' &&
          tab !== 'teams' &&
          !teams.loading && (
            <p className="muted mt-4">
              No team yet.{' '}
              {isAdmin
                ? 'Add one on the Teams tab to get started.'
                : 'An administrator needs to set one up.'}
            </p>
          )
        : (
            <div key={synced}>
              {tab === 'meetings' && <EosMeetings teamId={teamId} onChanged={reload} />}
              {tab === 'scorecard' && <EosScorecard teamId={teamId} onChanged={reload} />}
              {tab === 'rocks' && <EosRocks teamId={teamId} onChanged={reload} />}
              {tab === 'todos' && <EosTodos teamId={teamId} onChanged={reload} />}
              {tab === 'issues' && <EosIssues teamId={teamId} onChanged={reload} />}
              {tab === 'headlines' && <EosHeadlines teamId={teamId} onChanged={reload} />}
              {tab === 'templates' && isAdmin && <EosTemplates teamId={teamId} />}
            </div>
          )}

      {tab === 'teams' && isAdmin && <EosTeams key={synced} onChanged={reload} />}
    </>
  );
}
